import { useState } from 'react';
import {
  familyDashboard,
  familyLinkRestore,
  familyLinkRevoke,
  familyLinks,
  homePreferences,
  homePreferencesSave,
  residentFamilySharing,
  type ApiError,
  type HomePreferences,
  type ShareCategory,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, Waiting, notServed, pre, sx, useApi, useGuarded } from './kit';

// ── Your preferences (Aithora Home; design seIsPrefs T8692, hSet* L24812-24860) ──

const segH = (on: boolean) =>
  'flex:1 1 auto;display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0 16px;border-radius:12px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
  (on ? 'color:#FCFAF4;background:#2F5C97;border:1px solid #2F5C97;' : 'color:#3C454F;border-top:1px solid rgba(34,39,46,.14);');
const pillH = (on: boolean) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;min-width:92px;min-height:52px;padding:0 20px;border-radius:12px;font-size:16px;font-weight:700;letter-spacing:.04em;text-decoration:none;' +
  (on ? 'color:#FCFAF4;background:#2F5C97;border:1px solid #2F5C97;' : 'color:#3C454F;border-top:1px solid rgba(34,39,46,.14);');

export function PrefsSection() {
  const { client } = useSession();
  const { canWrite, roReason } = useGuarded();
  const q = useApi((c, s) => homePreferences(c, { signal: s }), []);
  const [err, setErr] = useState<ApiError | null>(null);
  if (!q.data && !q.error) return <Skeleton rows={5} label="Loading your preferences" />;
  if (!q.data) return notServed(q.error) ? <Waiting what="The Aithora Home preferences route" /> : <ErrLine error={q.error} />;
  const S = q.data;
  const put = (patch: Partial<HomePreferences>) => async () => {
    if (!canWrite) return;
    setErr(null);
    q.setData((p) => (p ? { ...p, ...patch } : p));
    try {
      // The server answers with what is really in force now (sharing reads back from the circle).
      const saved = await homePreferencesSave(client, { body: patch });
      q.setData(() => saved);
    } catch (e) {
      setErr(e as ApiError);
      q.reload();
    }
  };
  const people = S.contacts;
  const family = people.filter((p) => !p.onCall).map((p) => p.name.split(' ')[0]);
  const familyPhrase = family.length === 0 ? 'Nobody is on file to see it yet'
    : family.length === 1 ? family[0] + ' can see check-ins and reminders'
    : family.slice(0, -1).join(', ') + ' and ' + family[family.length - 1] + ' can see check-ins and reminders';
  const toggles: [keyof HomePreferences, string, string, boolean, string][] = [
    ['aloud', 'Read things aloud', 'Atlas speaks reminders and messages instead of only showing them.', S.aloud, 'Nothing is read aloud. Messages appear on screen only.'],
    ['night', 'Check on me overnight', 'A quiet check between 11pm and 6am. No sound unless something is wrong.', S.night, 'Nobody checks between 11pm and 6am. A fall overnight would not be noticed until morning.'],
    ['autoCall', 'Call for help automatically after a fall', 'If you do not answer within 60 seconds, Aithora calls for help without waiting for you.', S.autoCall, 'Aithora will wait for you to ask. If you are unconscious, nobody is called.'],
    ['share', 'Let my family see my day', familyPhrase + '. They never hear your conversations.', S.share, family.length ? 'Your family sees nothing day to day. They are still called in an emergency.' : 'Nothing is shared. There is nobody on file to share it with anyway.'],
  ];
  const zoom = S.text === 'Largest' ? 1.28 : S.text === 'Large' ? 1.14 : 1;
  // A preference Aithora does not act on yet says so, and what really happens, in place of its promise.
  const notActive = (k: keyof HomePreferences['enforcement']) => {
    const e = S.enforcement?.[k];
    return e && !e.enforced ? e.reason ?? 'Saved, but Aithora does not act on this yet.' : null;
  };
  const tag = (
    <span data-testid="pref-not-active" style={sx('display:inline-block;margin-left:10px;padding:2px 8px;border-radius:999px;font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.06);vertical-align:middle;')}>
      {I('NOT ACTIVE YET')}
    </span>
  );
  const ro = { 'aria-disabled': !canWrite || undefined, title: roReason ?? undefined };
  return (
    <div data-testid="settings-prefs" style={sx('zoom:' + zoom + ';display:flex;flex-direction:column;gap:0;min-width:0;max-width:900px;')}>
      <div style={sx('font-size:17px;line-height:1.5;color:#3C454F;text-wrap:pretty;padding-bottom:14px;')}>{I('These are yours to change. Nothing here is hidden from you, and nothing changes without you pressing it.')}</div>
      <div style={sx('min-width:0;padding:14px 0;border-top:1px solid rgba(34,39,46,.16);')}>
        <span style={sx('display:block;font-size:19px;font-weight:700;color:#22272E;')}>{I('Text size')}</span>
        <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>{I(notActive('text') ?? 'Makes this page and all of your Aithora Home screens bigger, straight away. It does not change what your family sees.')}</span>
        <span style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;min-width:0;')}>
          {(['Normal', 'Large', 'Largest'] as const).map((o) => (
            <a key={o} href="#" aria-pressed={S.text === o} {...ro} onClick={(e) => { pre(e); void put({ text: o })(); }} style={sx(segH(S.text === o))}>{I(o)}</a>
          ))}
        </span>
      </div>
      <div style={sx('min-width:0;padding:14px 0;border-top:1px solid rgba(34,39,46,.1);')}>
        <span style={sx('display:block;font-size:19px;font-weight:700;color:#22272E;')}>{I('Who gets called first')}{notActive('first') && tag}</span>
        <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>{I(notActive('first') ?? 'If something is wrong and you cannot answer, this is the person Aithora rings before anyone else.')}</span>
        {people.length > 1 && (
          <span style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;min-width:0;')}>
            {people.map((p) => (
              <a key={p.id} href="#" aria-pressed={S.first === p.id} {...ro} onClick={(e) => { pre(e); void put({ first: p.id })(); }} style={sx(segH(S.first === p.id))}>{I(p.name.split(' ')[0])}</a>
            ))}
          </span>
        )}
        {people.length < 2 && (
          <span style={sx('display:block;font-size:17px;line-height:1.5;color:#3C454F;margin-top:10px;text-wrap:pretty;')}>
            {I('The Aithora care line is called first. There is nobody else on file for ' + (S.memberName ?? 'you') + ', once the care team adds a family contact you can choose between them here.')}
          </span>
        )}
      </div>
      {toggles.map((t) => {
        const off = notActive(t[0] as keyof HomePreferences['enforcement']);
        return (
        <div key={t[0]} style={sx('display:flex;align-items:center;gap:16px;flex-wrap:wrap;row-gap:10px;min-width:0;padding:14px 0;border-top:1px solid rgba(34,39,46,.1);')}>
          <span style={sx('flex:1 1 240px;min-width:0;')}>
            <span style={sx('display:block;font-size:19px;font-weight:700;color:#22272E;')}>{I(t[1])}{off && tag}</span>
            <span style={sx('display:block;font-size:16px;line-height:1.45;margin-top:3px;text-wrap:pretty;color:' + (!off && !t[3] && (t[0] === 'night' || t[0] === 'autoCall') ? '#B3261E' : '#54606C') + ';')}>{I(off ?? (t[3] ? t[2] : t[4]))}</span>
          </span>
          <a href="#" role="switch" aria-checked={t[3]} aria-label={t[1]} {...ro} onClick={(e) => { pre(e); void put({ [t[0]]: !t[3] })(); }} style={sx(pillH(t[3]))}>{I(t[3] ? 'ON' : 'OFF')}</a>
        </div>
        );
      })}
      <ErrLine error={err} />
      <div style={sx('font-size:16px;line-height:1.5;color:#54606C;text-wrap:pretty;padding-top:14px;border-top:1px solid rgba(34,39,46,.16);')}>
        {I('Turning something off is always allowed, and we say plainly what you lose. If you change your mind, come back and press it again.')}
      </div>
    </div>
  );
}

// ── Who can see this (Aithora Home; design seIsConsumerConsent T9547, L22643-22667) ──

const SEES: [string, string][] = [['wellbeing', 'How you are doing'], ['moodTrend', 'your mood over two weeks'], ['highlights', 'the highlights you pass on'], ['lifeStory', 'your life story'], ['medicalStatus', 'your health status note']];

export function ConsentSection() {
  const { client, session } = useSession();
  const { canWrite, roReason } = useGuarded();
  const me = session?.user.id ?? '';
  // The route needs the person's own organization (it 400s without one).
  const links = useApi((c, s) => familyLinks(c, { params: { id: me }, query: { organizationId: session?.user.organizationId ?? undefined }, signal: s }), [me], !!me);
  const sharing = useApi((c, s) => residentFamilySharing(c, { params: { id: me }, signal: s }), [me], !!me);
  const [err, setErr] = useState<ApiError | null>(null);
  if (!links.data && !links.error) return <Skeleton rows={3} label="Loading who can see this" />;
  const on = sharing.data?.sharing ?? {};
  const seen = SEES.filter(([k]) => on[k as keyof typeof on]).map(([, w]) => w);
  const sees = seen.length === 0 ? 'Nothing yet' : seen.length === 1 ? seen[0] : seen.slice(0, -1).join(', ') + ', and ' + seen[seen.length - 1];
  const rows = (links.data?.links ?? []).map((l) => ({ ...l, label: (l.name ?? l.email ?? 'Family member') + (l.relationship ? ', your ' + l.relationship : '') }));
  return (
    <div data-testid="settings-consent" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:760px;')}>
      <div style={sx('font-size:16px;color:#3C454F;line-height:1.6;max-width:80ch;text-wrap:pretty;')}>
        {I('Nothing is shared until you say so. Turn a person on and they see what is listed, turn them off and they stop straight away.')}
      </div>
      <div style={sx('margin-top:13px;')}>
        {rows.map((p) => {
          const isOn = !p.revoked;
          return (
            <div key={p.familyUserId} style={sx('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:9px;padding:12px 0;border-top:1px solid rgba(34,39,46,.08);')}>
              <span style={sx('flex:1 1 180px;min-width:min(100%,180px);')}>
                <span style={sx('display:block;font-size:16px;font-weight:700;')}>{I(p.label)}</span>
                <span style={sx('display:block;font-size:16px;color:#54606C;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>{I(sees)}</span>
              </span>
              <a
                href="#"
                role="switch"
                aria-checked={isOn}
                aria-label={p.label}
                aria-disabled={!canWrite || undefined}
                title={roReason ?? undefined}
                onClick={(e) => {
                  pre(e);
                  if (!canWrite) return;
                  setErr(null);
                  const go = isOn
                    ? familyLinkRevoke(client, { params: { familyUserId: p.familyUserId }, body: { organizationId: session?.user.organizationId ?? null, residentId: me } })
                    : familyLinkRestore(client, { params: { familyUserId: p.familyUserId }, body: { residentId: me } });
                  go.then(() => links.reload()).catch((x) => setErr(x as ApiError));
                }}
                style={sx('display:flex;align-items:center;justify-content:center;min-width:84px;min-height:44px;padding:0 16px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;flex:0 0 auto;' + (isOn ? 'background:rgba(47,138,111,.2);color:#12503C;border:1px solid rgba(47,138,111,.4);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.22);'))}
              >
                {I(isOn ? 'On' : 'Off')}
              </a>
            </div>
          );
        })}
        {links.data && rows.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:12px 0;border-top:1px solid rgba(34,39,46,.08);')}>Nobody is linked to you yet, so nothing is shared.</div>}
      </div>
      <ErrLine error={err ?? links.error} />
      <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;margin-top:13px;padding-top:12px;border-top:1px solid rgba(34,39,46,.08);max-width:80ch;text-wrap:pretty;')}>
        {I('The one thing it will not keep to itself is a serious worry about your safety. If you say something that suggests you are unwell or at risk, a person is told.')}
      </div>
    </div>
  );
}

// ── What you can see (Family; design seIsFamilyView T9565, L22670-22685) ──

export function FamilyViewSection() {
  const q = useApi((c, s) => familyDashboard(c, { signal: s }), []);
  if (!q.data && !q.error) return <Skeleton rows={6} label="Loading what is shared with you" />;
  const first = q.data?.dashboard[0];
  const name = first?.resident.name?.split(' ')[0] ?? null;
  // The resident's own pronouns from /family/dashboard, so the page reads the way the
  // design words it ("she"). Without them it says "they", never a guess.
  const pn = (first?.resident as { pronouns?: 'she' | 'he' | 'they' } | undefined)?.pronouns ?? 'they';
  const P = {
    she: { sub: 'she', Sub: 'She', obj: 'her', pos: 'her', self: 'herself', is: 'is', does: 'does', uses: 'used', says: 'says', chose: 'chose' },
    he: { sub: 'he', Sub: 'He', obj: 'him', pos: 'his', self: 'himself', is: 'is', does: 'does', uses: 'used', says: 'says', chose: 'chose' },
    they: { sub: 'they', Sub: 'They', obj: 'them', pos: 'their', self: 'themselves', is: 'are', does: 'do', uses: 'used', says: 'say', chose: 'chose' },
  }[pn];
  const shared: Partial<Record<ShareCategory, boolean>> = first?.shared ?? {};
  const rows: [string, boolean, string][] = [
    ['Wellbeing status', !!shared.wellbeing, 'How ' + P.sub + ' ' + P.is + ' doing, day to day.'],
    ['Mood trend', !!shared.moodTrend, 'Two weeks at a glance, not the words ' + P.sub + ' ' + P.uses + '.'],
    ['Recent highlights', !!shared.highlights, 'Things ' + P.sub + ' ' + P.chose + ' to pass on.'],
    ['Conversation transcripts', false, 'The raw text of what ' + P.sub + ' ' + P.says + ' stays private to ' + P.obj + '.'],
    ['Medical record', false, 'Held by the building. Ask them, not us.'],
    ['Staff notes', false, 'Clinical documentation is not a family surface.'],
  ];
  return (
    <div data-testid="settings-family-view" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:820px;')}>
      {q.error && <ErrLine error={q.error} style="margin:0 0 10px;" />}
      <div style={sx('font-size:16px;color:#3C454F;line-height:1.6;max-width:88ch;text-wrap:pretty;')}>
        {I(name
          ? 'These are the things ' + name + ' has agreed to share with you. ' + P.Sub + ' can change any of it at any time, and you will not be notified when ' + P.sub + ' ' + P.does + '.'
          : 'Nobody has shared anything with you yet. When someone does, what they share is listed here.')}
      </div>
      <div style={sx('margin-top:13px;')}>
        {rows.map((r) => (
          <div key={r[0]} style={sx('display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;row-gap:5px;padding:10px 0;border-top:1px solid rgba(34,39,46,.08);')}>
            <span style={sx('flex:0 1 auto;min-width:0;width:186px;font-size:16px;font-weight:700;')}>{I(r[0])}</span>
            <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;flex:0 0 auto;' + (r[1] ? 'color:#12503C;background:rgba(47,138,111,.18);' : 'color:#54606C;background:rgba(34,39,46,.07);'))}>{I(r[1] ? 'Shared' : 'Not shared')}</span>
            <span style={sx('flex:1 1 180px;min-width:min(100%,180px);font-size:16px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>{I(r[2])}</span>
          </div>
        ))}
      </div>
      <div style={sx('box-sizing:border-box;background:rgba(79,138,214,.08);border-radius:10px;padding:12px 14px;margin-top:14px;')}>
        <div style={sx('font-size:16px;font-weight:700;')}>{I('If you want to see more')}</div>
        <div style={sx('font-size:16px;color:#3C454F;line-height:1.6;margin-top:5px;max-width:88ch;text-wrap:pretty;')}>
          {I('Ask ' + (name ?? 'them') + '. It is ' + P.pos + ' decision, and ' + P.sub + ' ' + P.does + ' not need a reason. If ' + P.sub + ' cannot make that decision ' + P.self + ', ' + P.pos + ' legal representative makes it, and being the closest relative is not the same thing.')}
        </div>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.6;margin-top:12px;max-width:88ch;text-wrap:pretty;')}>
        {I('If you are worried about how ' + P.sub + ' ' + P.is + ' being treated, tell the administrator at the building. You do not need evidence from this page first, and raising a concern in good faith is protected.')}
      </div>
    </div>
  );
}
